import React, { useEffect, useMemo, useRef } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import AxiosWrapper from "../../../../services/axios";
import marketing from "../../../../services/marketing";
import PixelsService from "../../../../services/pixels";
import AudioEyeSupport from "../../../../services/audioEyeSupport";
import AdobeScript from "../../../../services/adobeScript";
import "../../../../assets/prequal/prequaloffer.css";
import { InactiveDetection } from "../../../../services/inactiveDetection";
import { submitHash } from "../../../../services/submitHash";

function Offer() {
  const ada = new AudioEyeSupport();
  const Setting = useStoreState((state) => state.siteSettingsModel);
  const utms = new marketing();
  const history = useHistory();
  const axios = useMemo(() => new AxiosWrapper(), []);
  const processedHashRef = useRef(null);

  const { handleSubmit, register, formState: { errors }, setError } = useForm({ mode: "onSubmit" });

  const setApplicationResult = useStoreActions((actions) => actions.applicationResultModel.setApplicationResult);
  const setApplicantInfo = useStoreActions((actions) => actions.applicantModel.setApplicant);
  const PixelService = new PixelsService();
  const setInitPixels = useStoreActions((actions) => actions.setInitPixels);
  const initPixels = useStoreState((state) => state.initPixels);
  const getRoutingSettings = useStoreActions((actions) => actions.routingSettingsModel.getRoutingSettings);
  const termsStep = useStoreActions((actions) => actions.setDisplayPrequalTerms);
  const storeApplicantData = useStoreActions((actions) => actions.applicantModel.storeApplicantData);
  const adobe = new AdobeScript();

  InactiveDetection.setInactiveTitle("Make it count! Finish your application");

  useEffect(() => {
    if (!initPixels) {
      utms.mapper();
      PixelService.initialize()
        .pageView("/offer")
        .event("google", "tracking", "offer")
        .event("tiktok", null, "View Content");

      ada.setup();

      adobe.setup();
      getRoutingSettings({ Url: Setting.domain, Route: window.location.pathname });
      setInitPixels(true);
    }
  }, [initPixels, utms, PixelService, ada, adobe, getRoutingSettings, Setting, setInitPixels]);

  useEffect(() => {
    const hashString = axios.findParam("hash");
    setApplicantInfo({
      index: "hasCheckingAccount",
      value: axios.findParam("hascheckingaccount") || "",
    });

    if (hashString) {
      const newHash = hashString.replace(/^0x/, "");

      if (processedHashRef.current === newHash) {
        return;
      }

      processedHashRef.current = newHash;
      submitHash({
        apiClient: axios,
        hash: newHash,
        history,
        successPath: "/terms",
        setApplicationResult,
        setError,
        termsStep,
      });
      termsStep(true);
      history.push(`${Setting.MainPath}terms${window.location.search}`);
    } else {
      setApplicationResult({ result: "ERROR" });
    }
  }, [axios, history, setApplicantInfo, setApplicationResult, setError, termsStep]);

  const submitRez = async (data) => {
    const rezRequest = {
      reservationNumber: "",
      referenceNumber: data.referenceNumber,
      hashString: "",
      ssnLast4: data.ssn,
    };

    const responseCall = await axios.post("verify-prequal-application", rezRequest, true);
    const response = responseCall.Payload;
    const errorValidation = responseCall.Error;

    if (errorValidation) {
      setError("ssn", {
        type: "custom",
        message: errorValidation,
      });
      return;
    }

    if (response) {
      if (response.ApplicantId) {
        utms.updateSessionReport("confirmReservationAttempt1");
        setApplicationResult({
          product_code: response.ProductCode,
          ref_number: response.ApplicantId,
          result: "APPROVED",
          credit_limit: response.CreditLimit,
        });

        storeApplicantData({
          PrimaryPhone: response.MobileNo,
          PrimaryPhoneSms: response.MobileNo,
          SecondaryPhone: response.HomeNo,
          SecondaryPhoneSms: response.HomeNo,
        });

        termsStep(true);
        history.push(`${Setting.MainPath}terms${window.location.search}`);
      } else {
        setError("referenceNumber", {
          type: "custom",
          message: "Unable to verify offer. Please check reference number and try again.",
        });
      }
    }
  };

  return (
    <div id="prequal-offer-wrapper">
      <div
        className="row"
        id="header-text-container"
        style={{ backgroundColor: Setting.brandSettings.Primary }}
      >
        <h1 id="offer-header-text">Finish Right Where You Left Off!</h1>
      </div>

      <div className="container" id="offer-container">
        <form id="offer-form" onSubmit={handleSubmit(submitRez)}>
          <div className="row align-items-center">
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <div className="row d-md-none" id="offer-row-left">
                <b>
                  <ul>
                    <li>It won’t affect your credit score</li>
                    <li>It takes less than a minute to see if you pre-qualify</li>
                    <li>Review your offer and apply</li>
                  </ul>
                </b>
              </div>

              <div className="row" id="offer-row">
                <img
                  src={Setting.brandSettings.CardImage}
                  alt={`${Setting.brandSettings.ProductName} Card`}
                  className='col-lg-12'
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
              <div id="offer-row-left" className="d-none d-sm-none d-md-block row">
                <b>
                  <ul>
                    <li>It won’t affect your credit score</li>
                    <li>It takes less than a minute to see if you pre-qualify</li>
                    <li>Review your offer and apply</li>
                  </ul>
                </b>
              </div>

              {/* Reference Number */}
              <div className="mb-3">
                <label htmlFor="referenceNumber" className="form-label">
                  Reference Number
                </label>
                <div className="input-container">
                  <input
                    id="referenceNumber"
                    maxLength={9}
                    type="text"
                    className="form-control text-center"
                    placeholder="XXXXXXXXX"
                    {...register("referenceNumber", {
                      required: "Please enter a reference number.",
                      minLength: { value: 9, message: "Invalid Reservation Number" },
                    })}
                  />
                </div>
                {errors.referenceNumber && (
                  <div className="error-message text-center">
                    {errors.referenceNumber.message}
                  </div>
                )}
              </div>

              {/* SSN */}
              <div className="mb-3">
                <label htmlFor="ssn" className="form-label">
                  Last 4 of SSN
                </label>
                <div className="input-container">
                  <input
                    id="ssn"
                    maxLength={4}
                    type="text"
                    className="form-control text-center"
                    placeholder="Enter last 4 digits of your SSN"
                    {...register("ssn", {
                      required: "Last four of SSN are missing.",
                      minLength: { value: 4, message: "Please enter 4 digits." },
                    })}
                  />
                </div>
                {errors.ssn && (
                  <div className="error-message text-center">{errors.ssn.message}</div>
                )}
              </div>

              <button
                className="btn btn-block mt-2"
                style={{ backgroundColor: Setting.brandSettings.Secondary }}
                type="submit"
              >
                Return to Application
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Offer;
